import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, ArrowRight, Eye, EyeOff } from "lucide-react";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { api, setAuthToken } from "@/lib/api";

export default function LoginPage() {
    const { user, checking, refresh } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [website, setWebsite] = useState(""); // honeypot (must remain empty)
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    if (checking) return null;
    if (user) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            // send honeypot field along
            const { data } = await api.post("/auth/login", { email, password, website });
            if (data.access_token) setAuthToken(data.access_token);
            await refresh();
            toast.success("Benvenuto in Prime Vision Command Center.");
            navigate("/", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Credenziali non valide";
            toast.error(typeof msg === "string" ? msg : "Errore di login");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-5 bg-background text-textmain">
            {/* Left brand panel */}
            <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden border-r border-border/[var(--border-opacity)]">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] via-transparent to-azure/[0.10]" />
                <div
                    className="absolute inset-0 opacity-[0.18]"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg)",
                        backgroundSize: "cover", backgroundPosition: "center",
                        mixBlendMode: "multiply",
                    }}
                />
                <div className="relative flex flex-col justify-between p-14 w-full">
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
                            <div className="h-3 w-3 rounded-full bg-primary pv-pulse-ring" />
                        </div>
                        <div className="flex flex-col leading-tight">
                            <span className="text-[15px] font-medium tracking-tight">Prime Vision</span>
                            <span className="text-[10px] text-textsubtle uppercase tracking-[0.14em]">Command Center</span>
                        </div>
                    </div>

                    <div className="max-w-lg">
                        <div className="pv-eyebrow mb-4">Il cervello operativo della tua azienda</div>
                        <h1 className="pv-serif text-6xl leading-[1.02] tracking-tight">
                            Non un gestionale.<br />
                            <span className="text-primary">Un consulente</span> che analizza,<br />
                            capisce e agisce.
                        </h1>
                        <p className="mt-8 text-[15px] text-textmuted leading-relaxed">
                            CRM, marketing, vendite, customer success, analytics e automazioni.
                            Tutto collegato. Un solo posto. Un&apos;unica intelligenza.
                        </p>
                    </div>

                    <div className="flex items-center gap-4 text-[11px] text-textsubtle">
                        <div className="flex items-center gap-2">
                            <Sparkles size={12} strokeWidth={1.5} />
                            Prime Vision AI · Claude Sonnet 4.5
                        </div>
                        <span>·</span>
                        <span>SaaS · B2B · Ophthalmology</span>
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="col-span-1 lg:col-span-2 flex items-center justify-center px-8 py-12">
                <div className="w-full max-w-sm pv-enter">
                    <div className="pv-eyebrow mb-3">Accedi</div>
                    <h2 className="pv-serif text-4xl leading-tight tracking-tight mb-2">Bentornato.</h2>
                    <p className="text-[14px] text-textmuted mb-8">
                        Entra nel tuo command center per iniziare a leggere i dati come decisioni.
                    </p>

                    <form onSubmit={submit} className="space-y-4" autoComplete="on" noValidate>
                        {/* Honeypot — invisibile per gli umani, i bot lo riempiono */}
                        <div
                            aria-hidden="true"
                            style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
                        >
                            <label>
                                Sito web
                                <input
                                    tabIndex={-1}
                                    autoComplete="off"
                                    type="text"
                                    name="website"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                />
                            </label>
                        </div>

                        <label className="block">
                            <span className="text-[11px] uppercase tracking-wider text-textsubtle">Email</span>
                            <input
                                data-testid={TID.loginEmail}
                                type="email"
                                required
                                autoComplete="email"
                                placeholder="admin@primevision.it"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 w-full rounded-xl border border-border/[var(--border-opacity)] bg-surface px-3.5 py-2.5 text-[14px] outline-none focus:border-primary focus:pv-ring-primary transition-shadow"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-wider text-textsubtle">Password</span>
                            <div className="mt-1 relative">
                                <input
                                    data-testid={TID.loginPassword}
                                    type={showPw ? "text" : "password"}
                                    required
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full rounded-xl border border-border/[var(--border-opacity)] bg-surface px-3.5 py-2.5 pr-10 text-[14px] outline-none focus:border-primary focus:pv-ring-primary transition-shadow"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((s) => !s)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted"
                                >
                                    {showPw ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
                                </button>
                            </div>
                        </label>

                        <button
                            data-testid={TID.loginSubmit}
                            type="submit"
                            disabled={busy}
                            className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-2.5 text-[14px] font-medium hover:opacity-90 disabled:opacity-60 transition-opacity"
                        >
                            {busy ? "Accesso..." : "Entra"}
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </button>
                    </form>

                    <div className="mt-8 pv-divider" />
                    <div className="mt-6 text-[11px] text-textsubtle">
                        Protezione antibot attiva · sessione JWT sicura
                    </div>
                </div>
            </div>
        </div>
    );
}
